import Box from "@mui/material/Box";
import {
  Grid,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  IconButton,
  Paper,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WorkIcon from "@mui/icons-material/Work";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PreviewAttachmentDialog from "../PreviewAttachmentDialog";
import React, { useState, useEffect } from "react";
import { convertTimeFormat } from "../../utils/timeFormatter";
import { isMobile } from "react-device-detect";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import GeneralWithCategoriesForm from "./generalWithCategoriesForm";
import ElectricityForm from "./electricityForm";
import WaterForm from "./waterForm";
import NewCraftForm from "./newCraftForm";
import ClearanceCertificateForm from "./clearanceCertificateForm";
import ServiceWithDetailsForm from "./serviceWithDetailsForm";
import { filePathToFile } from "../../utils/pathToFileObject";

const OrderDetailsForm = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [generalServices, setGeneralServices] = useState({});
  const [craftsData, setCraftsData] = useState({});
  const [clearanceCertificateData, setClearanceCertificateData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [attachmentDetails, setAttachmentsDetails] = useState(undefined);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setIsLoading(true);
        const orderDetailsResponse = await axios.get(`/Application/${id}`);
        const orderDetailsData = orderDetailsResponse;
        setOrderDetails(orderDetailsData);

        const fragmentName = orderDetailsData.fragmentName
          ?.trim()
          .toLowerCase();

        if (
          [
            "general",
            "generalcategories",
            "electricity",
            "water",
            "servicewithdetails",
          ].includes(fragmentName)
        ) {
          const generalServicesResponse = await axios.get(
            `/Application/${id}/GeneralServices`
          );
          setGeneralServices(generalServicesResponse);
        }

        if (fragmentName === "newcrafts") {
          const craftsDataResponse = await axios.get(
            `/Application/${id}/Craft`
          );
          setCraftsData(craftsDataResponse);
        }

        if (fragmentName === "clearancecertificate") {
          const clearanceCertificateDataResponse = await axios.get(
            `/Application/${id}/ClearanceCertificate`
          );
          setClearanceCertificateData(clearanceCertificateDataResponse);
        }

        setIsLoading(false);
      } catch (e) {
        console.log("Error fetching order details", e);
      }
    };

    const fetchAttachments = async () => {
      try {
        setIsAttachmentsLoading(true);
        const attachmentsResponse = await axios.get(
          `/Application/${id}/Attachments?userOnly=true`
        );
        setAttachments(attachmentsResponse);
        setIsAttachmentsLoading(false);
      } catch (e) {
        console.log("Error fetching attachments", e);
        setIsAttachmentsLoading(false);
      }
    };

    // Call both functions simultaneously
    fetchOrderDetails();
    fetchAttachments();
  }, [id]);

  const serviceTypeName = orderDetails?.serviceType?.name;

  useEffect(() => {
    if (!serviceTypeName) {
      return;
    }

    if (location.state?.groupName === serviceTypeName) {
      return;
    }

    navigate(location.pathname, {
      replace: true,
      state: { ...location.state, groupName: serviceTypeName },
    });
  }, [serviceTypeName, location.pathname, location.state, navigate]);

  const showDialog = async (data) => {
    try {
      const fileObject = await filePathToFile(data.path, data.fileName);
      setAttachmentsDetails({ fileObject });
      setOpen(true);
    } catch (e) {}
  };

  const hideDialog = () => {
    setOpen(false);
    setAttachmentsDetails(undefined);
  };

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <CircularProgress size={80} />
      </Box>
    );
  }

  // Ensure orderDetails is defined before accessing its properties
  if (!orderDetails) {
    return null; // or show a fallback UI if needed
  }

  const isGeneralForm = orderDetails.fragmentName === "General";
  const isGeneralCategoryForm =
    orderDetails.fragmentName === "GeneralCategories";
  const isElectricityForm = orderDetails.fragmentName === "Electricity";
  const isWaterForm = orderDetails.fragmentName === "Water";
  const isNewCraft = orderDetails.fragmentName === "NewCrafts";
  const isClearanceCertificate =
    orderDetails.fragmentName === "ClearanceCertificate";
  const isServiceWithDetails =
    orderDetails.fragmentName === "ServiceWithDetails";

  return (
    <Stack gap={2}>
      <Box
        sx={{
          background: "#fff",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1.5,
          px: 3,
          width: isMobile ? "100%" : 300,
          borderRadius: "8px",
          border: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "primary.main" }}>
          معلومات الطلب
        </Typography>
      </Box>
      <Paper
        elevation={2}
        sx={{ 
          backgroundColor: "#fff", 
          p: 3, 
          borderRadius: "12px",
          border: "1px solid #e0e0e0"
        }}
      >
        <Grid spacing={3} container>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <DescriptionIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>نوع الطلب:</Typography>
                <Typography sx={{ color: "text.secondary" }}>{orderDetails.serviceType?.name}</Typography>
              </Stack>
            </Box>
          </Grid>

          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <BadgeIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>رقم الطلب:</Typography>
                <Typography sx={{ color: "text.secondary" }}>{orderDetails.subscriberServiceFullNumber}</Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <CalendarTodayIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>تاريخ الطلب:</Typography>
                <Typography sx={{ direction: "ltr", color: "text.secondary" }}>
                  {convertTimeFormat(orderDetails?.insertDate)}
                </Typography>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Divider />
      <Box
        sx={{
          background: "#fff",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1.5,
          px: 3,
          width: isMobile ? "100%" : 300,
          borderRadius: "8px",
          border: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography sx={{ fontWeight: "bold", color: "primary.main" }} variant="h6" component="div">
          بيانات مقدم الطلب
        </Typography>
      </Box>
      <Paper
        elevation={2}
        sx={{ 
          backgroundColor: "#fff", 
          p: 3, 
          borderRadius: "12px",
          border: "1px solid #e0e0e0"
        }}
      >
        <Grid spacing={3} container>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <PersonIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>اسم مقدم الطلب:</Typography>
                <Typography sx={{ color: "text.secondary" }}>{orderDetails.applicant?.fullName}</Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <DescriptionIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>نوع الوثيقة:</Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  {orderDetails.applicant?.documentType?.name}
                </Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <BadgeIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>رقم الوثيفة:</Typography>
                <Typography sx={{ color: "text.secondary" }}> {orderDetails.applicant?.documentNumber}</Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <PhoneIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>رقم الموبايل:</Typography>
                <Typography sx={{ color: "text.secondary" }}> {orderDetails.applicant?.phone1}</Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={6} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <EmailIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>البريد الالكتروني:</Typography>
                <Typography
                  title={orderDetails.applicant?.email}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "text.secondary",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {orderDetails.applicant?.email}
                </Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={6} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <HomeIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>العنوان:</Typography>
                <Typography sx={{ color: "text.secondary" }}>{`${orderDetails.applicant?.country?.name} - ${orderDetails.applicant?.city}`}</Typography>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      {(orderDetails.appliedBy || orderDetails.appliedByDocumentNumber || orderDetails.appliedByRelation) && (
        <>
          <Divider textAlign="left" sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: "bold", px: 2, color: "primary.main" }}>قدم بواسطة</Typography>
          </Divider>

          <Paper
            elevation={2}
            sx={{
              backgroundColor: "#fff",
              p: 3,
              borderRadius: "12px",
              border: "1px solid #e0e0e0"
            }}
          >
            <Grid
              container
              spacing={3}
            >
              <Grid item md={3} sm={4} xs={12}>
                <Stack direction={"row"} gap={1}>
                  <Typography sx={{ fontWeight: "bold" }}> قدم بواسطة:</Typography>
                  <Typography>{orderDetails.appliedBy} </Typography>
                </Stack>
              </Grid>
              <Grid item md={3} sm={4} xs={12}>
                <Stack direction={"row"} gap={1}>
                  <Typography sx={{ fontWeight: "bold" }}> رقم الوثيقة:</Typography>
                  <Typography> {orderDetails.appliedByDocumentNumber}</Typography>
                </Stack>
              </Grid>
              <Grid item md={3} sm={4} xs={12}>
                <Stack direction={"row"} gap={1}>
                  <Typography sx={{ fontWeight: "bold" }}>الصفة:</Typography>
                  <Typography>{orderDetails.appliedByRelation} </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
          <Divider />
        </>
      )}
      <Box
        sx={{
          background: "#fff",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1.5,
          px: 3,
          width: isMobile ? "100%" : 300,
          borderRadius: "8px",
          border: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "primary.main" }}>
          عنوان الطلب
        </Typography>
      </Box>
      <Paper
        elevation={2}
        sx={{
          backgroundColor: "#fff",
          p: 3,
          borderRadius: "12px",
          border: "1px solid #e0e0e0"
        }}
      >
        <Grid
          container
          spacing={3}
        >
          <Grid item md={6} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}>
                {" "}
                الغاية من تقديم الطلب :
              </Typography>
              <Typography> {orderDetails.serviceGoal}</Typography>
            </Stack>
          </Grid>
          <Grid item md={6} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}>
                {" "}
                ملاحظات المراجعة :
              </Typography>
              <Typography>{orderDetails.serviceReviewNote}</Typography>
            </Stack>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}> الموقع:</Typography>
              <Typography> {orderDetails?.location}</Typography>
            </Stack>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}>نوع المستفيد :</Typography>
              <Typography> {orderDetails?.beneficiaryType?.name}</Typography>
            </Stack>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}>رمز الاشتراك :</Typography>
              <Typography> {orderDetails?.citizenService?.code}</Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Divider />
      <Box
        sx={{
          background: "#fff",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1.5,
          px: 3,
          width: isMobile ? "100%" : 300,
          borderRadius: "8px",
          border: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "primary.main" }}>
          {" "}
          مسار الطلب
        </Typography>
      </Box>
      <Paper
        elevation={2}
        sx={{
          backgroundColor: "#fff",
          p: 3,
          borderRadius: "12px",
          border: "1px solid #e0e0e0"
        }}
      >
        <Grid
          container
          spacing={3}
        >
          <Grid item md={3} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}> النشاط الحالي:</Typography>
              <Typography>{orderDetails.activityName}</Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      <Divider />
      {(isGeneralForm || isGeneralCategoryForm) && (
        <GeneralWithCategoriesForm
          isGeneralCategoryForm={isGeneralCategoryForm}
          generalServices={generalServices}
        />
      )}
      {isElectricityForm && (
        <ElectricityForm generalServices={generalServices} />
      )}
      {isWaterForm && <WaterForm generalServices={generalServices} />}
      {isServiceWithDetails && (
        <ServiceWithDetailsForm generalServices={generalServices} />
      )}
      {isNewCraft && <NewCraftForm craftsData={craftsData} />}
      {isClearanceCertificate && (
        <ClearanceCertificateForm
          clearanceCertificateData={clearanceCertificateData}
        />
      )}

      <Box
        sx={{
          background: "#fff",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1.5,
          px: 3,
          width: isMobile ? "100%" : 300,
          borderRadius: "8px",
          border: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "primary.main" }}>
          المرفقات
        </Typography>
      </Box>

      {isAttachmentsLoading ? (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Paper
          elevation={2}
          sx={{
            backgroundColor: "#fff",
            p: 3,
            borderRadius: "12px",
            border: "1px solid #e0e0e0"
          }}
        >
          <Grid
            container
            spacing={3}
          >
            <Grid item md={3} sm={4} xs={8}>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                نوع المرفق
              </Typography>
            </Grid>
            <Grid item md={9} xs={4}>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                استعراض
              </Typography>
            </Grid>
            {attachments?.map((t, index) => {
              const hasAttachment = t?.fileName && t.path;
              return (
                <React.Fragment key={t.attachmentId || index}>
                  <Grid item md={3} xs={8}>
                    <Typography>{`${t.attachmentTypeName}${
                      t?.isMandatory ? " *" : ""
                    }`}</Typography>
                  </Grid>
                  <Grid item md={9} xs={4}>
                    <IconButton
                      color={hasAttachment ? "primary" : "default"}
                      disabled={!hasAttachment}
                      onClick={() => showDialog(t)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>
        </Paper>
      )}

      {open && (
        <PreviewAttachmentDialog
          open={open}
          handleClose={hideDialog}
          attachmentDetails={attachmentDetails}
        />
      )}
    </Stack>
  );
};

export default OrderDetailsForm;
